import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getReportedTasks, adminDeleteTask, adminRestoreTask } from '../services/api';
import './AdminTaskReports.css';

const AdminTaskReports = ({ standalone = true }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [reportedTasks, setReportedTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showTaskDetail, setShowTaskDetail] = useState(false);

  useEffect(() => {
    // Check if user is admin, if not redirect
    if (!user || user.role !== 'admin') {
      navigate('/');
      return;
    }

    const fetchReportedTasks = async () => {
      setLoading(true);
      try {
        const tasks = await getReportedTasks();
        setReportedTasks(tasks);
      } catch (err) {
        console.error('Error fetching reported tasks:', err);
        setError('Failed to load reported tasks. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchReportedTasks();
  }, [user, navigate]);

  const handleViewTask = (task) => {
    setSelectedTask(task);
    setShowTaskDetail(true);
  };

  const handleCloseDetail = () => {
    setSelectedTask(null);
    setShowTaskDetail(false);
  };

  const handleDeleteTask = async (taskId) => {
    if (window.confirm('Are you sure you want to permanently delete this task?')) {
      try {
        await adminDeleteTask(taskId);
        setReportedTasks(reportedTasks.filter(task => task._id !== taskId));
        if (selectedTask && selectedTask._id === taskId) {
          handleCloseDetail();
        }
      } catch (err) {
        console.error('Error deleting task:', err);
        alert('Failed to delete task. Please try again.');
      }
    }
  };

  const handleRestoreTask = async (taskId) => {
    try {
      await adminRestoreTask(taskId);
      // Update the task's autoRemoved status in the current list
      setReportedTasks(reportedTasks.map(task => 
        task._id === taskId
          ? { ...task, autoRemoved: false, autoRemovalReason: null }
          : task
      ));
      if (selectedTask && selectedTask._id === taskId) {
        setSelectedTask({ ...selectedTask, autoRemoved: false, autoRemovalReason: null });
      }
    } catch (err) {
      console.error('Error restoring task:', err);
      alert('Failed to restore task. Please try again.');
    }
  };

  // Function to get a summary of report reasons
  const getReportSummary = (reports) => {
    const reasons = reports.reduce((acc, report) => {
      acc[report.reason] = (acc[report.reason] || 0) + 1;
      return acc;
    }, {});
    
    return Object.entries(reasons)
      .map(([reason, count]) => `${reason} (${count})`)
      .join(', ');
  };

  return (
    <div className={`admin-reports-container ${!standalone ? 'embedded' : ''}`}>
      {standalone && (
        <div className="admin-reports-header">
          <h1>Reported Community Tasks</h1>
        </div>
      )}

      {loading ? (
        <div className="loading">Loading reported tasks...</div>
      ) : error ? (
        <div className="error-message">{error}</div>
      ) : reportedTasks.length === 0 ? (
        <div className="no-reports">No reported tasks found.</div>
      ) : (
        <div className="reported-tasks-list">
          <table className="reports-table">
            <thead>
              <tr>
                <th>Task</th>
                <th>Type</th>
                <th>Created By</th>
                <th>Reports</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {reportedTasks.map(task => (
                <tr key={task._id} className={task.autoRemoved ? 'auto-removed' : ''}>
                  <td>
                    <div className="task-title" onClick={() => handleViewTask(task)}>
                      {task.title}
                    </div>
                  </td>
                  <td>{task.type}</td>
                  <td>{task.createdBy?.username || 'Unknown'}</td>
                  <td>
                    <span className="report-count">{task.reports?.length || 0}</span>
                    {task.reports?.length > 0 && (
                      <div className="report-summary">
                        {getReportSummary(task.reports)}
                      </div>
                    )}
                  </td>
                  <td>
                    {task.autoRemoved ? (
                      <span className="removed-status">
                        Removed: {task.autoRemovalReason === 'poor_rating' ? 'Poor Rating' : 'Too Many Reports'}
                      </span>
                    ) : (
                      <span className="active-status">Active</span>
                    )}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className="view-button"
                        onClick={() => handleViewTask(task)}
                      >
                        View
                      </button>
                      
                      {task.autoRemoved ? (
                        <button 
                          className="restore-button"
                          onClick={() => handleRestoreTask(task._id)}
                        >
                          Restore
                        </button>
                      ) : (
                        <button 
                          className="remove-button"
                          onClick={() => handleDeleteTask(task._id)}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Task Detail Modal */}
      {showTaskDetail && selectedTask && (
        <div className="modal-overlay">
          <div className="modal-content task-detail-modal">
            <div className="modal-header">
              <h2>{selectedTask.title}</h2>
              <button className="close-button" onClick={handleCloseDetail}>×</button>
            </div>
            
            <div className="task-info">
              <div className="task-meta">
                <p><strong>Type:</strong> {selectedTask.type}</p>
                <p><strong>Difficulty:</strong> {selectedTask.difficulty}</p>
                <p><strong>Created By:</strong> {selectedTask.createdBy?.username || 'Unknown'}</p>
                <p><strong>Status:</strong> {selectedTask.autoRemoved ? 'Auto-removed' : 'Active'}</p>
                {selectedTask.autoRemoved && (
                  <p><strong>Removal Reason:</strong> {
                    selectedTask.autoRemovalReason === 'poor_rating' 
                      ? 'Poor Rating (5+ downvotes, 0 upvotes)' 
                      : 'Multiple Reports (10+ reports)'
                  }</p>
                )}
              </div>
              
              <div className="task-stats">
                <p><strong>Upvotes:</strong> {selectedTask.upvotes || 0}</p>
                <p><strong>Downvotes:</strong> {selectedTask.downvotes || 0}</p>
                <p><strong>Completions:</strong> {selectedTask.completions || 0}</p>
                <p><strong>Attempts:</strong> {selectedTask.attempts || 0}</p>
              </div>
            </div>
            
            <div className="task-description">
              <h3>Description:</h3>
              <p>{selectedTask.description}</p>
            </div>
            
            <div className="task-content">
              <h3>Content:</h3>
              <pre>{selectedTask.content}</pre>
            </div>
            
            <div className="task-solution">
              <h3>Solution:</h3>
              <pre>{selectedTask.solution}</pre>
            </div>
            
            {selectedTask.reports && selectedTask.reports.length > 0 && (
              <div className="task-reports">
                <h3>Reports ({selectedTask.reports.length}):</h3>
                <ul>
                  {selectedTask.reports.map((report, index) => (
                    <li key={index} className={`report-item ${report.reason}`}>
                      <div className="report-header">
                        <span className="report-reason">{report.reason}</span>
                        <span className="report-date">
                          {new Date(report.reportedAt).toLocaleString()}
                        </span>
                      </div>
                      {report.description && (
                        <div className="report-description">{report.description}</div>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            <div className="modal-actions">
              {selectedTask.autoRemoved ? (
                <button 
                  className="restore-button"
                  onClick={() => handleRestoreTask(selectedTask._id)}
                >
                  Restore Task
                </button>
              ) : (
                <button 
                  className="delete-button"
                  onClick={() => handleDeleteTask(selectedTask._id)}
                >
                  Delete Task
                </button>
              )}
              <button 
                className="close-button"
                onClick={handleCloseDetail}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTaskReports;